import { useEffect, useState } from 'react';

import {
  ChangelogDocument,
  ChangelogEntry,
  getChangelog,
  setChangelogExclusion,
} from '../api/changelog';
import { useAuth } from '../auth/useAuth';

const CATEGORY_ORDER = ['Breaking', 'Deprecated', 'New Feature', 'Enhancement', 'Bugfix'] as const;

const CATEGORY_STYLES: Record<
  string,
  { bg: string; color: string; border: string; label: string }
> = {
  Breaking: {
    bg: 'var(--danger-bg)',
    color: 'var(--danger-text)',
    border: 'var(--danger-border)',
    label: 'Breaking',
  },
  Deprecated: {
    bg: 'var(--warning-bg)',
    color: 'var(--warning-text)',
    border: 'var(--warning-border)',
    label: 'Deprecated',
  },
  'New Feature': {
    bg: 'var(--success-bg)',
    color: 'var(--success-text)',
    border: 'var(--success-border)',
    label: 'New Feature',
  },
  Enhancement: {
    bg: 'var(--accent-surface)',
    color: 'var(--accent-text)',
    border: 'var(--accent-border)',
    label: 'Enhancement',
  },
  Bugfix: {
    bg: 'var(--info-bg)',
    color: 'var(--info-text)',
    border: 'var(--info-border)',
    label: 'Bugfix',
  },
};

function CategoryBadge({ category }: { category: string }) {
  const s = CATEGORY_STYLES[category] ?? CATEGORY_STYLES.Enhancement;
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: '0.7rem',
        padding: '2px 8px',
        borderRadius: 999,
        fontWeight: 500,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}

interface EntryCardProps {
  entry: ChangelogEntry;
  /** Admins may hide an entry from end users; everyone else sees no control. */
  canEdit: boolean;
  busy: boolean;
  onToggle: (entry: ChangelogEntry) => void;
}

function EntryCard({ entry, canEdit, busy, onToggle }: EntryCardProps) {
  const isHighSeverity = entry.category === 'Breaking' || entry.category === 'Deprecated';
  const hidden = Boolean(entry.excluded);
  return (
    <div
      style={{
        // A hidden entry is drawn back, never dimmed with opacity — the design
        // system treats opacity on text as an accessibility regression.
        border: `1px solid ${
          hidden
            ? 'var(--border-subtle)'
            : isHighSeverity
              ? CATEGORY_STYLES[entry.category].border
              : 'var(--border-subtle)'
        }`,
        borderRadius: 8,
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        background: hidden
          ? 'var(--bg-surface)'
          : isHighSeverity
            ? CATEGORY_STYLES[entry.category].bg
            : 'transparent',
        color: hidden ? 'var(--text-muted)' : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <CategoryBadge category={entry.category} />
        <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {entry.jiraKey}
        </span>
        {entry.removalDate && (
          <span
            style={{
              fontSize: '0.7rem',
              padding: '2px 8px',
              borderRadius: 999,
              background: 'var(--danger-bg)',
              color: 'var(--danger-text)',
              border: '1px solid var(--danger-border)',
            }}
          >
            Removal: {entry.removalDate}
          </span>
        )}
        {hidden && (
          <span
            style={{
              fontSize: '0.7rem',
              padding: '2px 8px',
              borderRadius: 999,
              background: 'var(--bg-surface-2)',
              color: 'var(--text-muted)',
              border: '1px solid var(--border-strong)',
            }}
            title="Not shown to end users. Survives a changelog refresh."
          >
            Hidden
          </span>
        )}
        {canEdit && (
          <button
            disabled={busy}
            style={{
              marginLeft: 'auto',
              padding: '3px 10px',
              borderRadius: 999,
              border: '1px solid var(--border-strong)',
              background: 'transparent',
              color: busy ? 'var(--text-muted)' : 'var(--text-secondary)',
              fontSize: '0.72rem',
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
            title={
              hidden
                ? 'Show this entry to end users again'
                : 'Hide this entry from end users, without changing the Jira ticket'
            }
            onClick={() => onToggle(entry)}
          >
            {busy ? '…' : hidden ? 'Restore' : 'Hide'}
          </button>
        )}
      </div>

      <p style={{ margin: 0, fontWeight: 500, lineHeight: 1.5 }}>{entry.summary}</p>

      {entry.affectedEndpoints && (
        <div style={{ fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
            Affected endpoints:{' '}
          </span>
          <code style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            {entry.affectedEndpoints}
          </code>
        </div>
      )}

      {entry.migrationNotes && (
        <div
          style={{
            marginTop: 4,
            padding: '10px 14px',
            borderRadius: 6,
            background: 'var(--bg-surface-2)',
            borderLeft: `3px solid ${CATEGORY_STYLES[entry.category]?.color ?? 'var(--accent-text)'}`,
          }}
        >
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'var(--text-muted)',
              marginBottom: 4,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Migration notes
          </div>
          <pre
            style={{
              margin: 0,
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
              fontFamily: 'inherit',
            }}
          >
            {entry.migrationNotes}
          </pre>
        </div>
      )}
    </div>
  );
}

interface CategorySectionProps {
  category: string;
  entries: ChangelogEntry[];
  canEdit: boolean;
  busyKey: string | null;
  onToggle: (entry: ChangelogEntry) => void;
}

function CategorySection({ category, entries, canEdit, busyKey, onToggle }: CategorySectionProps) {
  if (!entries.length) {
    return null;
  }
  return (
    <section style={{ marginBottom: 32 }}>
      <h2
        style={{
          fontSize: '0.85rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-muted)',
          marginBottom: 12,
        }}
      >
        {category} <span style={{ fontWeight: 400 }}>({entries.length})</span>
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {entries.map((e) => (
          <EntryCard
            busy={busyKey === e.jiraKey}
            canEdit={canEdit}
            entry={e}
            key={e.jiraKey}
            onToggle={onToggle}
          />
        ))}
      </div>
    </section>
  );
}

interface Props {
  apiId: string;
  versionId: string;
}

export default function ChangelogContent({ apiId, versionId }: Props) {
  const { role } = useAuth();
  const [doc, setDoc] = useState<ChangelogDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  // Only admins may hide entries. Non-admins never receive the control, and
  // end users never receive the entries at all — the API filters them out.
  const canEdit = role === 'admin';

  const handleToggle = (entry: ChangelogEntry) => {
    const next = !entry.excluded;
    setBusyKey(entry.jiraKey);
    setToggleError(null);
    setChangelogExclusion(apiId, versionId, entry.jiraKey, next)
      .then((result) => {
        // Reconcile against the authoritative list rather than trusting the
        // single toggle — another admin may have changed something meanwhile.
        const hidden = new Set(result.excluded);
        setDoc((prev) =>
          prev
            ? {
                ...prev,
                entries: prev.entries.map((e) => ({ ...e, excluded: hidden.has(e.jiraKey) })),
              }
            : prev,
        );
      })
      .catch((err: unknown) => {
        setToggleError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setBusyKey(null));
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    getChangelog(apiId, versionId)
      .then(setDoc)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
          setError('no-changelog');
        } else {
          setError(msg);
        }
      })
      .finally(() => setLoading(false));
  }, [apiId, versionId]);

  const grouped = doc
    ? CATEGORY_ORDER.reduce<Record<string, ChangelogEntry[]>>((acc, cat) => {
        acc[cat] = doc.entries.filter((e) => e.category === cat);
        return acc;
      }, {})
    : {};

  const breakingCount = grouped.Breaking?.length ?? 0;
  const deprecatedCount = grouped.Deprecated?.length ?? 0;
  const hiddenCount = doc?.entries.filter((e) => e.excluded).length ?? 0;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 24px', width: '100%' }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 8 }}>Changelog</h1>
        {doc && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
            {doc.entries.length} change{doc.entries.length !== 1 ? 's' : ''} — version{' '}
            {doc.versionId}
            {(breakingCount > 0 || deprecatedCount > 0) && (
              <>
                {' — '}
                {breakingCount > 0 && (
                  <span style={{ color: 'var(--danger-text)' }}>{breakingCount} breaking</span>
                )}
                {breakingCount > 0 && deprecatedCount > 0 && ', '}
                {deprecatedCount > 0 && (
                  <span style={{ color: 'var(--warning-text)' }}>{deprecatedCount} deprecated</span>
                )}
              </>
            )}
            {hiddenCount > 0 && ` — ${hiddenCount} hidden from end users`}
          </p>
        )}
      </div>

      {loading && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading changelog…</p>
      )}

      {!loading && error === 'no-changelog' && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          No changelog has been generated for this version yet.
        </p>
      )}

      {!loading && error && error !== 'no-changelog' && (
        <p
          style={{
            fontSize: '0.875rem',
            padding: '10px 14px',
            borderRadius: 6,
            background: 'var(--danger-bg)',
            color: 'var(--danger-text)',
            border: '1px solid var(--danger-border)',
          }}
        >
          {error}
        </p>
      )}

      {toggleError && (
        <p
          style={{
            fontSize: '0.85rem',
            padding: '10px 14px',
            marginBottom: 16,
            borderRadius: 6,
            background: 'var(--danger-bg)',
            color: 'var(--danger-text)',
            border: '1px solid var(--danger-border)',
          }}
        >
          Could not update visibility: {toggleError}
        </p>
      )}

      {!loading && doc && (
        <>
          {doc.entries.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              No changelog entries for this version.
            </p>
          ) : (
            CATEGORY_ORDER.map((cat) => (
              <CategorySection
                busyKey={busyKey}
                canEdit={canEdit}
                category={cat}
                entries={grouped[cat] ?? []}
                key={cat}
                onToggle={handleToggle}
              />
            ))
          )}
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 16 }}>
            Last refreshed {new Date(doc.refreshedAt).toLocaleString()}
          </p>
        </>
      )}
    </div>
  );
}
